import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, Container, Row, Col, Badge, Button, Card, Spinner, Modal } from 'react-bootstrap';
import { fetchTeacherById, toggleFollowTeacher } from '../../../store/slices/teachersSlice';
import { getFollowersApi, getFollowingApi, getTeacherReviewsApi } from '../../../api/teachers';
import { StarRating } from '../../marketplace/components/StarRating';
import { CourseCard } from '../../marketplace/components/CourseCard';
import './teachers-details.scss';

import teacherPlaceholder from '../../../assets/teacher_profile.png';
import thumbPlaceholder from '../../../assets/video_thumbnail.png';

export const TeachersDetails = () => {
    const location = useLocation();
    const params = useParams();
    const queryParams = new URLSearchParams(location.search);
    const id = location.state?.id || params.id || queryParams.get('id');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentTeacher: data, loading, error } = useSelector((state) => state.teachers);
    const { isAuthenticated } = useSelector((state) => state.auth);
    const [showFullBio, setShowFullBio] = useState(false);

    // Modals & Interaction States
    const [showFollowersModal, setShowFollowersModal] = useState(false);
    const [followersList, setFollowersList] = useState([]);
    const [followersLoading, setFollowersLoading] = useState(false);

    const [showFollowingModal, setShowFollowingModal] = useState(false);
    const [followingList, setFollowingList] = useState([]);
    const [followingLoading, setFollowingLoading] = useState(false);

    const [showReviewsModal, setShowReviewsModal] = useState(false);
    const [reviewsList, setReviewsList] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);

    useEffect(() => {
        if (id) {
            dispatch(fetchTeacherById(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (error && (error.toLowerCase().includes('token') || error.toLowerCase().includes('unauthorized') || error.toLowerCase().includes('expired'))) {
            if (!isAuthenticated) {
                // If it's a hard 401 and they're unauthenticated, redirect.
                // navigate('/login', { state: { from: window.location.pathname } });
            }
        }
    }, [error, isAuthenticated, navigate]);

    const handleFollowToggle = () => {
        if (!isAuthenticated) {
            navigate('/login', { state: { from: window.location.pathname } });
            return;
        }
        if (activeData?.teacher?._id) {
            dispatch(toggleFollowTeacher({ id: activeData.teacher._id, isFollowing: activeData.is_follow }));
        }
    };

    const handleShare = async (platform) => {
        const shareUrl = data?.shareUrlWeb || window.location.href;
        const text = `Check out ${name}'s profile on Yoospeak!`;

        if (platform === 'copy') {
            navigator.clipboard.writeText(shareUrl);
            alert('Link copied to clipboard!');
            return;
        }

        if (navigator.share && platform === 'native') {
            try {
                await navigator.share({ title: 'Yoospeak Instructor', text, url: shareUrl });
            } catch (err) { console.log('Share failed:', err); }
            return;
        }

        const urls = {
            fb: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
            tw: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
            wa: `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + shareUrl)}`
        };

        if (urls[platform]) window.open(urls[platform], '_blank');
    };

    const toggleBio = () => setShowFullBio(!showFullBio);

    const activeData = data || {
        teacher: {
            _id: '',
            full_name: 'Loading Instructor...',
            profile_picture: '',
            profile_cover_image: '',
        },
        languages: [],
        teacher_rating: 0,
        follower_count: 0,
        following_count: 0,
        country: '',
        my_posts: [],
        upcoming_classes: [],
        courses: [],
        teacher_reviews: { average_rating: 0, total_reviews: 0 },
        slot_counts: { booked: 0, total: 0 },
        is_follow: false,
        shareUrlApp: '',
        shareUrlWeb: ''
    };

    const {
        teacher,
        languages,
        teacher_rating,
        follower_count,
        following_count,
        country,
        my_posts = [],
        upcoming_classes = [],
        courses = [],
        teacher_reviews,
        shareUrlWeb,
        isSubscriptionEnabled,
        slot_counts
    } = activeData;

    const postsCount = activeData?.posts_count ?? activeData?.total_posts ?? activeData?.my_posts_count ?? teacher?.posts_count ?? my_posts.length;

    const profilePic = teacher?.profile_picture || teacherPlaceholder;
    const coverPic = teacher?.profile_cover_image || null;
    const name = teacher?.full_name || teacher?.name || activeData?.name || 'Instructor';
    const rating = parseFloat(teacher_reviews?.average_rating || teacher_rating || 0);
    const reviewCount = teacher_reviews?.total_reviews || 0;

    // Handlers for stats bar clicks
    const handleOpenFollowers = async () => {
        setShowFollowersModal(true);
        setFollowersLoading(true);
        const targetId = id || activeData?.teacher?._id;
        try {
            const res = await getFollowersApi(targetId);
            const list = res?.data?.followers || res?.followers || (Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));
            setFollowersList(Array.isArray(list) ? list : []);
        } catch (e) {
            console.log('Error fetching followers:', e);
            setFollowersList([]);
        } finally {
            setFollowersLoading(false);
        }
    };

    const handleOpenFollowing = async () => {
        setShowFollowingModal(true);
        setFollowingLoading(true);
        const targetId = id || activeData?.teacher?._id;
        try {
            const res = await getFollowingApi(targetId);
            const list = res?.data?.following || res?.following || (Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));
            setFollowingList(Array.isArray(list) ? list : []);
        } catch (e) {
            console.log('Error fetching following list:', e);
            setFollowingList([]);
        } finally {
            setFollowingLoading(false);
        }
    };

    const handleScrollToPosts = () => {
        const postsElem = document.getElementById('teacher-posts-section');
        if (postsElem) {
            postsElem.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const formatUserCountry = (c) => {
        if (!c) return '';
        if (typeof c === 'object' && c.name) return c.name;
        if (typeof c === 'string') {
            const trimmed = c.trim();
            if (/^[0-9a-fA-F]{24}$/.test(trimmed)) return '';
            return trimmed;
        }
        return '';
    };

    const handleViewProfile = (userItem) => {
        setShowFollowersModal(false);
        setShowFollowingModal(false);
        const userObj = userItem.user || userItem.follower || userItem.following || userItem.followingId || userItem.followerId || userItem;
        const targetId = userObj._id || userObj.id || userObj.user_id || userItem._id || userItem.id || userItem.followingId || userItem.followerId;
        const rawRole = (userObj.user_type || userObj.role || userItem.user_type || userItem.role || '').toLowerCase();

        if (targetId) {
            if (rawRole === 'student') {
                navigate('/student', { state: { id: targetId } });
            } else {
                navigate('/teachers-details', { state: { id: targetId } });
            }
        }
    };

    const handleOpenReviews = async () => {
        setShowReviewsModal(true);
        setReviewsLoading(true);
        const targetId = id || activeData?.teacher?._id;
        try {
            const res = await getTeacherReviewsApi(targetId);
            const list = res?.data?.reviews || res?.reviews || res?.data || [];
            if (Array.isArray(list) && list.length > 0) {
                setReviewsList(list);
            } else if (activeData?.teacher_reviews?.reviews && activeData.teacher_reviews.reviews.length > 0) {
                setReviewsList(activeData.teacher_reviews.reviews);
            } else {
                setReviewsList([]);
            }
        } catch (e) {
            console.log('Error fetching reviews:', e);
            setReviewsList([]);
        } finally {
            setReviewsLoading(false);
        }
    };

    if (error || (!loading && !data)) {
        return (
            <Container className="py-5 text-center">
                <div className="alert alert-danger">{error || 'Teacher not found'}</div>
                <Link to="/marketplace" className="btn btn-primary">Back to Marketplace</Link>
            </Container>
        );
    }

    // Derive specialties from keypoints in classes or hardcoded if missing
    const specialties = (upcoming_classes[0]?.keypoints || ['Conversational English', 'Business English', 'English for Traveling']).filter(k => k !== 'trending' && k !== 'recommended');

    return (
        <section className="teachers-details-page py-4 position-relative">
            {loading && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center" style={{
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.4)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    transition: 'all 0.3s ease-in-out'
                }}>
                    <div className="p-4 rounded-4 shadow bg-white bg-opacity-75 text-center d-flex flex-column align-items-center" style={{ border: '1px solid rgba(255, 255, 255, 0.5)' }}>
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <span className="mt-3 fw-bold text-navy" style={{ letterSpacing: '0.5px' }}>Loading profile...</span>
                    </div>
                </div>
            )}
            <Container fluid="xl">
                {coverPic && (
                    <div className="teacher-cover-image mb-4 rounded-3 overflow-hidden shadow-sm" style={{ height: '300px' }}>
                        <img src={coverPic} alt="Cover" className="w-100 h-100 object-fit-cover" />
                    </div>
                )}

                <div className="layout-wrapper">
                    {/* Main Content Column */}
                    <div className="teacher-main-content">
                        {/* Teacher Header */}
                        <div className="teacher-header text-gray-900 d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                            <div className="teacher-profile d-flex align-items-center gap-4">
                                <div className="profile-img-container position-relative">
                                    <img
                                        src={profilePic}
                                        alt={name}
                                        className="border border-4 border-white rounded-circle shadow-lg"
                                        style={{ height: '100px', width: '100px', objectFit: 'cover' }}
                                        onError={(e) => { e.target.src = teacherPlaceholder; }}
                                    />
                                    {isSubscriptionEnabled && (
                                        <Badge bg="warning" className="position-absolute bottom-0 end-0 rounded-circle p-2 shadow-sm" title="Subscription Enabled">
                                            <i className="bi bi-star-fill text-white"></i>
                                        </Badge>
                                    )}
                                </div>
                                <span className="teacher-info text-capitalize">
                                    <h1 className="teacher-name fw-bold fs-2 text-gray-900 mb-1">{name}</h1>
                                    <div className="teacher-tagline text-gray-600 fs-5">
                                        {teacher?.user_type === 'teacher' ? 'Professional Language Mentor' : 'Creative Content Instructor'}
                                    </div>
                                </span>
                            </div>
                            <div className="country-info d-flex align-items-center gap-2 bg-white px-4 py-2 rounded-pill shadow-sm border">
                                <span className="country-flag fs-4">{teacher?.country_code === 'IN' ? '🇮🇳' : '🌐'}</span>
                                <span className="country-name fw-bold text-gray-700">
                                    {typeof country === 'object' ? country?.name : country || 'International'}
                                </span>
                            </div>
                        </div>

                        {/* Summary Text / Experience */}
                        <div className="experience-badges text-base text-gray-700 mb-4 d-flex flex-wrap align-items-center gap-2">
                            <Badge bg="primary" className="fw-semibold px-4 py-2 rounded-pill">
                                {teacher?.experience > 0 ? `${teacher.experience}+ Years Experience` : 'Expert Mentor'}
                            </Badge>
                            {teacher?.level && (
                                <Badge bg="secondary" className="fw-semibold px-4 py-2 rounded-pill shadow-sm">
                                    {teacher.level} Level
                                </Badge>
                            )}
                            {teacher?.is_blue_verified && (
                                <Badge bg="info" className="d-flex align-items-center gap-2 px-4 py-2 rounded-pill shadow-sm">
                                    <i className="bi bi-patch-check-fill"></i> Blue Verified
                                </Badge>
                            )}
                            {slot_counts?.total > 0 && (
                                <Badge bg="success" className="bg-opacity-10 text-success border border-success border-opacity-25 px-4 py-2 rounded-pill">
                                    {slot_counts.total} Slots Available
                                </Badge>
                            )}
                        </div>

                        {/* Info Bar / Stats */}
                        <div className="stats-info-bar bg-white border border-light-subtle d-flex flex-wrap align-items-center justify-content-between gap-3 rounded-4 mb-5 px-4 py-4 shadow-sm">
                            <div className="dynamic-info d-flex align-items-center gap-4">
                                <div
                                    className="stat-item text-center px-2 py-1 rounded-3 hover-bg-light transition-all"
                                    style={{ cursor: 'pointer' }}
                                    onClick={handleOpenFollowers}
                                    title="Click to view list of followers"
                                >
                                    <div className="fw-black fs-3 text-primary">{follower_count}</div>
                                    <div className="text-xs text-gray-500 fw-bold text-uppercase tracking-wider">Followers</div>
                                </div>
                                <div
                                    className="stat-item text-center ps-4 border-start px-2 py-1 rounded-3 hover-bg-light transition-all"
                                    style={{ cursor: 'pointer' }}
                                    onClick={handleOpenFollowing}
                                    title="Click to view following list"
                                >
                                    <div className="fw-black fs-3 text-primary">{following_count}</div>
                                    <div className="text-xs text-gray-500 fw-bold text-uppercase tracking-wider">Following</div>
                                </div>
                                <div
                                    className="stat-item text-center ps-4 border-start px-2 py-1 rounded-3 hover-bg-light transition-all"
                                    style={{ cursor: 'pointer' }}
                                    onClick={handleScrollToPosts}
                                    title="Click to scroll down to posts"
                                >
                                    <div className="fw-black fs-3 text-primary">{postsCount}</div>
                                    <div className="text-xs text-gray-500 fw-bold text-uppercase tracking-wider">Posts</div>
                                </div>
                            </div>
                            <div className="rating-info d-flex flex-column align-items-end gap-1 px-3 py-2 rounded-3">
                                <div className="stars d-flex gap-1 fs-5">
                                    <StarRating rating={rating} />
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="rating-val text-gray-900 fw-black fs-4">{rating.toFixed(1)}</span>
                                    <span className="rating-count text-secondary text-sm fw-bold">
                                        ({reviewCount} Verified Reviews)
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Specialties Section (Accordion) */}
                        {/* <div className="specialties-section mb-5">
                            <div className="main-heading mb-4 fs-4 fw-bold">My Specialties</div>
                            <Accordion className="custom-accordion border-0 shadow-sm rounded-4 overflow-hidden">
                                {specialties.map((spec, idx) => (
                                    <Accordion.Item eventKey={idx.toString()} key={idx} className="border-bottom border-light">
                                        <Accordion.Header className="fw-semibold">{spec}</Accordion.Header>
                                        <Accordion.Body className="text-gray-600">
                                            Tailored sessions focused on {spec.toLowerCase()}, helping you achieve fluency and confidence through practical exercises and real-world scenarios.
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </div> */}

                        {/* About Me Section */}
                        <div className="about-section mb-5">
                            <div className="main-heading mb-3 fs-4 fw-bold">About me</div>
                            <div className="description-text text-gray-700 bg-white p-4 rounded-4 border shadow-sm">
                                <div className={`bio-content ${!showFullBio && teacher?.description?.length > 400 ? 'text-truncate-4' : ''}`} style={{ whiteSpace: 'pre-line' }}>
                                    {teacher?.description || 'No detailed bio provided yet. Explore my courses and classes to learn more about my teaching style.'}
                                </div>
                                {teacher?.description?.length > 400 && (
                                    <Button variant="link" className="p-0 mt-3 text-decoration-none fw-bold text-primary" onClick={toggleBio}>
                                        {showFullBio ? 'Read less' : 'Read more'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* My Languages Section */}
                        <div className="language-section mb-5">
                            <div className="main-heading mb-3 fs-4 fw-bold">My Languages</div>
                            <div className="d-flex flex-wrap gap-3">
                                {(() => {
                                    const langList = Array.isArray(languages)
                                        ? languages
                                        : (typeof languages === 'string' ? languages.split(',') : []);
                                    if (langList.length === 0) return <div className="text-muted">No languages specified.</div>;
                                    return langList.map((lang, idx) => {
                                        const langName = typeof lang === 'object' ? lang.name : lang;
                                        if (!langName) return null;
                                        return (
                                            <div key={idx} className="lang-item bg-white border border-light-subtle d-flex align-items-center gap-3 px-4 py-2 rounded-pill shadow-sm">
                                                <div className="lang-icon bg-primary bg-opacity-10 text-primary rounded-circle flex-center" style={{ width: '32px', height: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <i className="bi bi-translate"></i>
                                                </div>
                                                <span className="lang-name fw-bold text-gray-800">{langName.trim()}</span>
                                                <Badge bg="success" className="bg-opacity-10 text-success fw-semibold px-3 py-1">Proficient</Badge>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>

                        {/* Upcoming Classes Section */}
                        {upcoming_classes.length > 0 && (
                            <div className="upcoming-classes mb-5">
                                <div className="main-heading mb-4 fs-4 fw-bold">Live Upcoming Classes</div>
                                <Row className="g-4">
                                    {upcoming_classes.map((cls) => (
                                        <Col md={6} key={cls._id}>
                                            <Card className="border-0 shadow-sm h-100 rounded-4 overflow-hidden card-hover">
                                                <div className="bg-primary bg-opacity-10 p-4 pb-2">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <Badge bg={cls.type === 'one_to_one' ? 'info' : 'primary'} className="rounded-pill px-3 py-2">
                                                            {cls.type === 'one_to_one' ? '1-on-1' : 'Group Session'}
                                                        </Badge>
                                                        <span className="fs-3 fw-black text-primary">${cls.cost || 0}</span>
                                                    </div>
                                                    <h3 className="fs-5 fw-bold text-gray-900 mb-0">{cls.title}</h3>
                                                </div>
                                                <Card.Body className="p-4 pt-0">
                                                    <div className="text-sm text-gray-600 my-3">
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <i className="bi bi-calendar3 text-primary"></i>
                                                            <span className="fw-medium">{cls.start_date}</span>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="bi bi-clock text-primary"></i>
                                                            <span className="fw-medium">{cls.start_time} - {cls.end_time}</span>
                                                        </div>
                                                    </div>
                                                    <Button variant="primary" className="w-100 rounded-3 py-2 fw-bold">Book This Session</Button>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}

                        {/* My Posts (Video Feed) */}
                        <div id="teacher-posts-section" className="my-posts-section mb-5">
                            <div className="main-heading mb-4 fs-4 fw-bold">Latest Posts</div>
                            {my_posts.length > 0 ? (
                                <Row className="g-4 row-cols-1 row-cols-sm-2 row-cols-md-3">
                                    {my_posts.map((post) => (
                                        <Col key={post._id}>
                                            <div className="video-card rounded-4 overflow-hidden shadow-sm border h-100 bg-white shadow-hover transition-all">
                                                <div className="ratio ratio-16x9 position-relative cursor-pointer">
                                                    <img
                                                        src={post.thumbnailUrl || thumbPlaceholder}
                                                        alt={post.title}
                                                        className="object-fit-cover"
                                                        onError={(e) => { e.target.src = thumbPlaceholder; }}
                                                    />
                                                    <div className="play-btn-overlay position-absolute top-50 start-50 translate-middle bg-white bg-opacity-90 rounded-circle flex-center shadow-lg" style={{ width: '45px', height: '45px' }}>
                                                        <i className="bi bi-play-fill text-primary fs-3"></i>
                                                    </div>
                                                </div>
                                                <div className="p-3">
                                                    <div className="fw-bold text-gray-900 text-truncate mb-2">{post.title}</div>
                                                    <div className="text-xs text-gray-500 d-flex justify-content-between align-items-center">
                                                        <span className="d-flex align-items-center gap-1">
                                                            <i className="bi bi-eye"></i> {post.views} views
                                                        </span>
                                                        <span className="bg-light px-2 py-1 rounded">{new Date(post.createdAt).toLocaleDateString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            ) : (
                                <div className="p-4 bg-white rounded-4 border text-muted text-center">
                                    No posts uploaded yet by this instructor.
                                </div>
                            )}
                        </div>

                        {/* Courses Section */}
                        {courses.length > 0 && (
                            <div className="courses-section">
                                <div className="main-heading mb-4 fs-4 fw-bold">Popular Courses</div>
                                <Row className="g-4 row-cols-1 row-cols-sm-2">
                                    {courses.map((course) => (
                                        <Col key={course._id}>
                                            <CourseCard course={course} />
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}
                    </div>

                    {/* Sidebar Column */}
                    <div className="sidebar-sticky-col">
                        <Card className="sidebar-wrapper border-0 rounded-4 shadow-xl overflow-hidden">
                            <div className="bg-primary text-white p-5 text-center position-relative overflow-hidden">
                                <div className="decoration-circle position-absolute" style={{ top: '-20px', right: '-20px', width: '100px', height: '100px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }}></div>
                                <div className="text-sm text-uppercase fw-black opacity-75 mb-1 tracking-widest">Base Rate</div>
                                <div className="d-flex align-items-center justify-content-center gap-3">
                                    <span className="display-4 fw-black">$20.00</span>
                                </div>
                            </div>

                            <Card.Body className="p-4 p-xl-5">
                                <div className="cta-buttons d-flex flex-column gap-3 mb-4">
                                    <Button
                                        className="btn-custom py-3 fs-5 fw-black shadow-lg rounded-3"
                                        onClick={() => navigate(`/classes?teacherId=${activeData?.teacher?._id || ''}`)}
                                    >
                                        <i className="bi bi-lightning-charge-fill me-2"></i> Book Trial Lesson
                                    </Button>
                                    <div className="d-flex gap-3">
                                        <Button
                                            variant={activeData?.is_follow ? "primary" : "outline-primary"}
                                            className="px-4 py-3 rounded-3 shadow-hover transition-all"
                                            onClick={handleFollowToggle}
                                        >
                                            <i className={`bi ${activeData?.is_follow ? "bi-person-check-fill shadow-glow" : "bi-person-plus-fill"}`}></i>
                                        </Button>
                                    </div>
                                </div>

                                <hr className="my-5 opacity-10" />

                                <div className="share-section text-center">
                                    <div className="text-xs fw-black text-gray-400 mb-4 text-uppercase tracking-widest">Share this profile</div>
                                    <div className="share-actions d-flex justify-content-center gap-3">
                                        {[
                                            { icon: 'bi-facebook', key: 'fb' },
                                            { icon: 'bi-twitter-x', key: 'tw' },
                                            { icon: 'bi-whatsapp', key: 'wa' },
                                            { icon: 'bi-link-45deg', key: 'copy' }
                                        ].map((soc) => (
                                            <div
                                                key={soc.key}
                                                className="share-icon-circle pointer transition-all shadow-sm"
                                                onClick={() => handleShare(soc.key)}
                                            >
                                                <i className={`bi ${soc.icon} fs-5`}></i>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-5 pt-4 border-top text-center">
                                    <a
                                        href={activeData?.shareUrlApp || '#'}
                                        className="btn btn-link text-decoration-none text-primary fw-black small text-uppercase tracking-wider"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <i className="bi bi-phone-fill me-2 fs-5"></i> Open In App
                                    </a>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </Container>

            {/* Followers Modal */}
            <Modal show={showFollowersModal} onHide={() => setShowFollowersModal(false)} centered className="rounded-4">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-5 text-gray-900">
                        <i className="bi bi-people-fill text-primary me-2"></i> Followers ({followersList.length})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    {followersLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : followersList.length === 0 ? (
                        <div className="text-center text-muted py-4">No followers found.</div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {followersList.map((item, idx) => {
                                const userObj = item.user || item.follower || item;
                                const avatar = userObj.profile_picture || item.profile_picture || teacherPlaceholder;
                                const userName = userObj.full_name || item.full_name || 'Student User';
                                const userCountry = formatUserCountry(userObj.country || item.country);
                                const rawRole = userObj.user_type || item.user_type || 'student';
                                const userRole = rawRole.charAt(0).toUpperCase() + rawRole.slice(1);
                                const subtext = userCountry ? `${userCountry} • ${userRole}` : userRole;
                                return (
                                    <div key={userObj._id || item._id || idx} className="d-flex align-items-center justify-content-between p-3 rounded-3 border bg-light bg-opacity-50">
                                        <div
                                            className="d-flex align-items-center gap-3"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleViewProfile(item)}
                                        >
                                            <img
                                                src={avatar}
                                                alt={userName}
                                                className="rounded-circle border"
                                                style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                                                onError={(e) => { e.target.src = teacherPlaceholder; }}
                                            />
                                            <div>
                                                <div className="fw-bold text-gray-900">{userName}</div>
                                                <div className="text-xs text-gray-500">{subtext}</div>
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="rounded-pill px-3 py-2 fw-bold text-nowrap flex-shrink-0"
                                            onClick={() => handleViewProfile(item)}
                                        >
                                            View Profile
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {/* Following Modal */}
            <Modal show={showFollowingModal} onHide={() => setShowFollowingModal(false)} centered className="rounded-4">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-5 text-gray-900">
                        <i className="bi bi-person-check-fill text-primary me-2"></i> Following ({followingList.length})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    {followingLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : followingList.length === 0 ? (
                        <div className="text-center text-muted py-4">Not following anyone yet.</div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {followingList.map((item, idx) => {
                                const userObj = item.user || item.following || item;
                                const avatar = userObj.profile_picture || item.profile_picture || teacherPlaceholder;
                                const userName = userObj.full_name || item.full_name || 'Instructor';
                                const userCountry = formatUserCountry(userObj.country || item.country);
                                const rawRole = userObj.user_type || item.user_type || 'teacher';
                                const userRole = rawRole.charAt(0).toUpperCase() + rawRole.slice(1);
                                const subtext = userCountry ? `${userCountry} • ${userRole}` : userRole;
                                return (
                                    <div key={userObj._id || item._id || idx} className="d-flex align-items-center justify-content-between p-3 rounded-3 border bg-light bg-opacity-50">
                                        <div
                                            className="d-flex align-items-center gap-3"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleViewProfile(item)}
                                        >
                                            <img
                                                src={avatar}
                                                alt={userName}
                                                className="rounded-circle border"
                                                style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                                                onError={(e) => { e.target.src = teacherPlaceholder; }}
                                            />
                                            <div>
                                                <div className="fw-bold text-gray-900">{userName}</div>
                                                <div className="text-xs text-gray-500">{subtext}</div>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                            <Badge bg="success" className="bg-opacity-10 text-success rounded-pill px-3 py-2 fw-semibold text-nowrap">
                                                Following
                                            </Badge>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="rounded-pill px-3 py-2 fw-bold text-nowrap"
                                                onClick={() => handleViewProfile(item)}
                                            >
                                                View Profile
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </section>
    );
};